import React, {useEffect, useMemo, useState} from "react";
import {useTranslation} from "react-i18next";

//redux:
import {useDispatch, useSelector} from "react-redux";
import {mediaItemPageActions} from "../../../redux/MediaItemPage/mediaItemRedux";

//components:
import {NetgraphWrapper} from "../../../widgets";
import WarningRoundedIcon from "@material-ui/icons/WarningRounded";
import MediaPageSearchBar from "./MediaPageSearchBar";
import NetgraphNavbar from "./NetgraphNavbar";

//helpers:
import {sendDataLayer} from "../../../util/tagmanager";
import Tour from "reactour";
import {updateParameters} from "../../../redux_slices/userParametersSlice";
import {disableBodyScroll, enableBodyScroll} from "body-scroll-lock";
import TranscriptsDropdown from "./TranscriptsDropdown";
import {HasPermission} from "../../../../_metronic/_helpers";
import SearchBarResults from "./SearchBarResults";

const NetgraphContainer = ({
    page,
    videoSrcReview,
    netgraphRatio,
    showMasterNodesLabels,
    netgraphContainer,
    netgraphContainerHeight,
    videoStarted,
    insightsOptions,
    selectedInsights,
    setSelectedInsights,
    prepareGetAppearancesData,
    timelineHighlight,
    handleMatchClick,
    showSuggestion,
    setShowSuggestion,
    setNodeAppearances,
    resetTimeline,
    currentRefinement,
    refine,
    hits,
    searching,
}) => {
    const {t} = useTranslation();
    //local state:
    const [netgraphDataFilteredBySpeakers, setNetgraphDataFilteredBySpeakers] = useState([]);

    //redux:
    const dispatch = useDispatch();
    //store:
    const {
        info = {},
        netGraphData = [],
        netgraphDataError = {},
        videoNetgraphConfig = {},
        netgraphDataLoading = false,
        netgraphConfigLoading = false,
    } = useSelector(state => state.mediaItemPage);

    const contextSelector = useSelector(state => state.context);
    const credentialSelector = useSelector(state => state.context.credential);

    const [showModal, setShowModal] = useState(false);

    useEffect(() => {
        if (!credentialSelector.tours?.viewer) {
            setShowModal(true);
            dispatch(updateParameters({values: {tours: {...(credentialSelector.tours ?? {}), viewer: true}}}));
        }
    }, []);

    const selectedInsightsKeys = useMemo(() => {
        return selectedInsights?.map(d => d.key);
    }, [selectedInsights]);

    const tagmanagerData = {
        video_id: info.id,
        video_name: info.title,
    };

    const setNetgraphNodeSelection = nodeSelected => {
        const netgraphHasData = netGraphData?.length;
        const isDifferentFromLastSearch = currentRefinement?.toLowerCase() !== nodeSelected?.name?.toLowerCase();
        if (netgraphHasData && nodeSelected.name && isDifferentFromLastSearch) {
            prepareGetAppearancesData(nodeSelected);

            sendDataLayer(
                {...tagmanagerData, event: "video_netgraph_select", selectionNode: nodeSelected.name},
                {context: contextSelector}
            );
        }
    };

    const unselectNode = () => {
        dispatch(mediaItemPageActions.setIsConceptualSearch(false));
        refine("");
        resetTimeline();
        sendDataLayer({...tagmanagerData, event: "netgraph_reset"}, {context: contextSelector});
    };

    const netgraphContent = () => {
        if (
            !videoStarted &&
            ((page === "videos" && info && info.streaming_url) || (page === "review" && videoSrcReview))
        ) {
            return (
                <div className="d-flex flex-column mx-auto h-100 justify-content-center position-relative w-100">
                    <div className="w-100 h-100 rounded position-absolute d-flex justify-content-center align-items-center px-6 z-index-3">
                        <div className="alert alert-light font-size-h5 font-weight-bold z-index-3" role="alert">
                            {t("VIDEOPAGE.PLAY_VIDEO")}
                        </div>
                    </div>
                </div>
            );
        } else if (netgraphDataError && netgraphDataError.text) {
            return (
                <div className="d-flex flex-column mx-auto h-100 justify-content-center position-relative w-100">
                    <div className="w-100 h-100 rounded position-absolute d-flex justify-content-center align-items-center px-6 z-index-2">
                        <div
                            className={`alert alert-${netgraphDataError.type ||
                                "info"} font-size-h5 font-weight-bold z-index-3`}
                        >
                            <WarningRoundedIcon color="inherit" className="mr-5" />
                            {netgraphDataError.text}
                        </div>
                    </div>
                </div>
            );
        } else {
            return (
                <NetgraphWrapper
                    config={videoNetgraphConfig}
                    showMasterNodesLabels={showMasterNodesLabels}
                    data={netgraphDataFilteredBySpeakers}
                    isLoading={netgraphDataLoading || netgraphConfigLoading}
                    features={selectedInsightsKeys || []}
                    nodeSelectionCallback={setNetgraphNodeSelection}
                    nodeUnselectionCallback={unselectNode}
                    selectedNodeName={currentRefinement}
                    aspectRatio={netgraphRatio}
                    onEmptyShowIcon={true}
                />
            );
        }
    };
    const steps = [
        {
            selector: ".mediaItemPage_container",
            content: t("TOURS.VIEWER.1"),
        },
        {
            selector: ".videoSearchBar_container",
            content: t("TOURS.VIEWER.2"),
        },
        {
            selector: "#netgraph",
            content: t("TOURS.VIEWER.3"),
        },
        {
            selector: "#netgraph",
            content: t("TOURS.VIEWER.4"),
            action: node => {
                const button = document.getElementsByClassName("BADGES.HIGHLIGHTS.PEOPLE");
                if (button[0]) button[0].click();
            },
        },
        {
            selector: "#netgraph",
            content: t("TOURS.VIEWER.5"),
            action: node => {
                const button = document.getElementsByClassName("BADGES.HIGHLIGHTS.HIGHLIGHTS");
                if (button[0]) button[0].click();
            },
        },
        {
            selector: "#netgraph",
            content: t("TOURS.VIEWER.6"),
            action: node => {
                const ball = document.querySelectorAll("#netgraph text");
                if (ball[1] && ball[1].click) ball[1].click();
            },
        },
        {
            selector: ".progress.timeline_bar",
            content: t("TOURS.VIEWER.7"),
        },

        // ...
    ];
    return (
        <>
            <div className="col-12 col-md-4">
                <div
                    className="card card-custom card-stretch category-network-chart-container gutter-b"
                    ref={netgraphContainer}
                    style={{height: netgraphContainerHeight || 500}}
                >
                    {page === "videos" ? (
                        <div className="position-absolute w-100 h-100 left-0 top-0">
                            <div className="d-flex flex-column w-100 h-100 position-relative">
                                <div className="videoSearchBar_container d-flex">
                                    <HasPermission permissionsToCheck={["videos.downloadTranscript"]}>
                                        <div className="zindex-1 d-flex mr-4">
                                            <TranscriptsDropdown itemId={info.id} title={info.title} />
                                        </div>
                                    </HasPermission>
                                    <div className="w-100">
                                        <MediaPageSearchBar
                                            setShowSuggestion={setShowSuggestion}
                                            setNodeAppearances={setNodeAppearances}
                                            setShowModal={setShowModal}
                                            resetTimeline={resetTimeline}
                                            currentRefinement={currentRefinement}
                                            refine={refine}
                                            searching={searching}
                                        />
                                    </div>
                                </div>
                                <SearchBarResults
                                    showSuggestion={showSuggestion}
                                    handleMatchClick={handleMatchClick}
                                    currentRefinement={currentRefinement}
                                    hits={hits}
                                    searching={searching}
                                />
                            </div>
                        </div>
                    ) : null}

                    <div className={`netgraph_videopage_container ${showSuggestion ? "fade-out" : ""}`}>
                        <div id="netgraph_navbar">
                            <NetgraphNavbar
                                page={page}
                                insightsOptions={insightsOptions}
                                selectedInsights={selectedInsights}
                                setSelectedInsights={setSelectedInsights}
                                setNetgraphDataFilteredBySpeakers={setNetgraphDataFilteredBySpeakers}
                                showModal={showModal}
                            />
                        </div>

                        {netgraphContent()}
                    </div>
                </div>
            </div>

            {useMemo(
                () => (
                    <Tour
                        steps={steps}
                        isOpen={showModal}
                        onRequestClose={() => setShowModal(false)}
                        onAfterOpen={target => disableBodyScroll(target)}
                        onBeforeClose={target => enableBodyScroll(target)}
                        scrollDuration={100}
                        rounded={15}
                        disableInteraction={true}
                        closeWithMask={false}
                    />
                ),
                [showModal, setShowModal]
            )}
        </>
    );
};
export default NetgraphContainer;
